import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { behaviors } from '../services/api';

const AddBehavior = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [behaviorCatalog, setBehaviorCatalog] = useState([]);
  const [newRecord, setNewRecord] = useState({
    behaviorId: '',
    intensity: 1,
    duration: 5,
    antecedent: '',
    consequence: '',
    notes: '',
    recorded_at: new Date().toISOString()
  });

  useEffect(() => {
    fetchBehaviorCatalog();
  }, []);

  const fetchBehaviorCatalog = async () => {
    try {
      const response = await behaviors.getCatalog();
      setBehaviorCatalog(response.data.data);
    } catch (error) {
      console.error('Error fetching behavior catalog:', error);
      setError('Falha ao carregar catálogo de comportamentos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const response = await behaviors.createRecord(patientId, newRecord);
      alert('Registro de comportamento criado com sucesso');
      navigate(`/dashboard/patients/${patientId}`);
    } catch (error) {
      console.error('Error creating behavior record:', error);
      setError('Falha ao criar registro de comportamento');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(`/dashboard/patients/${patientId}`)}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Adicionar Registro de Comportamento</h1>
      </div>

      {error && (
        <div className="mb-6 flex items-center text-red-600">
          <AlertCircle className="mr-2" size={20} />
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Behavior Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Comportamento
            </label>
            <select
              value={newRecord.behaviorId}
              onChange={(e) => setNewRecord({ ...newRecord, behaviorId: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              required
            >
              <option value="">Selecione um tipo de comportamento</option>
              {behaviorCatalog.map((behavior) => (
                <option key={behavior.id} value={behavior.id}>
                  {behavior.name} - {behavior.category}
                </option>
              ))}
            </select>
          </div>

          {/* Intensity and Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Intensidade (1-5)
              </label>
              <input
                type="number"
                min="1"
                max="5"
                value={newRecord.intensity}
                onChange={(e) => setNewRecord({ ...newRecord, intensity: parseInt(e.target.value) })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duração (minutos)
              </label>
              <input
                type="number"
                min="1"
                value={newRecord.duration}
                onChange={(e) => setNewRecord({ ...newRecord, duration: parseInt(e.target.value) })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                required
              />
            </div>
          </div>

          {/* Antecedent */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Antecedente
            </label>
            <textarea
              value={newRecord.antecedent}
              onChange={(e) => setNewRecord({ ...newRecord, antecedent: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              placeholder="O que aconteceu antes do comportamento?"
              required
            />
          </div>

          {/* Consequence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Consequência
            </label>
            <textarea
              value={newRecord.consequence}
              onChange={(e) => setNewRecord({ ...newRecord, consequence: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              placeholder="O que aconteceu depois do comportamento?"
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notas Adicionais
            </label>
            <textarea
              value={newRecord.notes}
              onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              placeholder="Quaisquer observações ou notas adicionais..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-brand-700 text-white rounded-md text-sm font-medium hover:bg-brand-800 flex items-center disabled:opacity-50"
            >
              <Save size={16} className="mr-2" />
              Salvar Registro de Comportamento
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBehavior; 